import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ListIcon, Calendar, Search, X, ArrowUp, ArrowDown } from 'lucide-react';
import type { SearchResults } from '@/hooks/useSearch';
import { getAvatarSrc } from '@/lib/storage/avatarUrl';
import { getCreatorProfileUrl } from '@/lib/utils/urls';
import { FadeImage } from '@/components/ui/fade-image';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTrendingTokens, type TrendingToken } from '@/hooks/useTrendingTokens';
import { useSparklineData } from '@/hooks/useSparklineData';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { useRecentlyViewed, type RecentlyViewedToken } from '@/hooks/useRecentlyViewed';
import { useRecentlyLaunched, type RecentlyLaunchedToken } from '@/hooks/useRecentlyLaunched';
import { SparklineSVG } from '@/components/search/SparklineSVG';
import { formatMarketCap, formatCurrency, formatPercentChange, formatTimeAgoWithSuffix } from '@/lib/utils/formatters';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipTrigger,
  TooltipStatsContent,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface SearchDropdownProps {
  results: SearchResults;
  isLoading: boolean;
  error: string | null;
  query: string;
  onClose: () => void;
  onSearchSelect?: (query: string) => void;
}

function truncateMint(address: string): string {
  if (address.length <= 8) return address;
  return `${address.slice(0, 4)}...dex`;
}

type NavItem =
  | { kind: 'trending'; index: number; data: TrendingToken }
  | { kind: 'recent-search'; index: number; query: string }
  | { kind: 'recent-viewed'; index: number; data: RecentlyViewedToken }
  | { kind: 'recent-launched'; index: number; data: RecentlyLaunchedToken }
  | { kind: 'search-token'; index: number; data: SearchResults['tokens'][0] }
  | { kind: 'search-creator'; index: number; data: SearchResults['creators'][0] }
  | { kind: 'search-list'; index: number; data: SearchResults['lists'][0] };

export function SearchDropdown({
  results,
  isLoading,
  error,
  query,
  onClose,
  onSearchSelect,
}: SearchDropdownProps) {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const isBrowseMode = !query || query.trim().length < 2;

  // Internal data fetching for browse mode
  const { tokens: trendingTokens } = useTrendingTokens({ limit: 8 });
  const trendingMints = useMemo(
    () => trendingTokens.map((t) => t.mintAddress),
    [trendingTokens]
  );
  const { sparklines } = useSparklineData(trendingMints);
  const { searches: recentSearches, removeSearch, clearAll: clearSearches } =
    useRecentSearches();
  const { viewedTokens, clearAll: clearViewed } = useRecentlyViewed();
  const { tokens: launchedTokens } = useRecentlyLaunched();

  // Build flat navigation list
  const navItems: NavItem[] = useMemo(() => {
    if (!isBrowseMode) {
      return [
        ...results.tokens.map((t, i) => ({
          kind: 'search-token' as const,
          index: i,
          data: t,
        })),
        ...results.creators.map((c, i) => ({
          kind: 'search-creator' as const,
          index: i,
          data: c,
        })),
        ...results.lists.map((l, i) => ({
          kind: 'search-list' as const,
          index: i,
          data: l,
        })),
      ];
    }

    const items: NavItem[] = [];
    trendingTokens.forEach((t, i) =>
      items.push({ kind: 'trending', index: i, data: t })
    );
    recentSearches.forEach((s, i) =>
      items.push({ kind: 'recent-search', index: i, query: s.query })
    );
    viewedTokens.forEach((v, i) =>
      items.push({ kind: 'recent-viewed', index: i, data: v })
    );
    launchedTokens.forEach((l, i) =>
      items.push({ kind: 'recent-launched', index: i, data: l })
    );
    return items;
  }, [
    isBrowseMode,
    results,
    trendingTokens,
    recentSearches,
    viewedTokens,
    launchedTokens,
  ]);

  // Reset selection on query or data change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results, query]);

  const activateItem = useCallback((item: NavItem) => {
    switch (item.kind) {
      case 'trending':
        navigate(`/token/${item.data.mintAddress}`);
        break;
      case 'recent-search':
        onSearchSelect?.(item.query);
        return; // Don't close - let search run
      case 'recent-viewed':
        navigate(`/token/${item.data.mint}`);
        break;
      case 'recent-launched':
        navigate(`/token/${item.data.mintAddress}`);
        break;
      case 'search-token':
        navigate(`/token/${item.data.mint}`);
        break;
      case 'search-creator':
        navigate(
          getCreatorProfileUrl(item.data.address, item.data.username)
        );
        break;
      case 'search-list':
        navigate(`/lists/${item.data.slug}`);
        break;
    }
    onClose();
  }, [navigate, onClose, onSearchSelect]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (navItems.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < navItems.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : navItems.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < navItems.length) {
            activateItem(navItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navItems, selectedIndex, onClose, activateItem]);

  // Compute flat index for a nav item
  const getFlatIndex = (kind: string, itemIndex: number): number => {
    let offset = 0;
    for (const item of navItems) {
      if (item.kind === kind && item.index === itemIndex) return offset;
      offset++;
    }
    return -1;
  };

  const isSelected = (kind: string, itemIndex: number) =>
    getFlatIndex(kind, itemIndex) === selectedIndex;

  const rowClass = (kind: string, itemIndex: number) =>
    `flex items-center gap-3 ${isMobile ? 'px-2 py-2.5 min-h-[48px]' : 'px-3 py-2 min-h-[44px]'} rounded-md transition-colors cursor-pointer ${
      isSelected(kind, itemIndex) ? 'bg-white/10' : 'hover:bg-white/5'
    }`;

  // Tooltip wrapper: only on desktop
  const MaybeTooltip = ({
    children,
    stats,
  }: {
    children: React.ReactElement;
    stats?: { label: string; value: string; change?: string; changeDirection?: 'positive' | 'negative' }[];
  }) => {
    if (isMobile || !stats || stats.length === 0) return children;
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipStatsContent stats={stats} />
      </Tooltip>
    );
  };

  // ── Loading state ──
  if (isLoading && query.length >= 2) {
    return (
      <DropdownShell ref={containerRef} isMobile={isMobile}>
        <div className="flex items-center justify-center gap-2 text-muted-foreground p-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Searching...</span>
        </div>
      </DropdownShell>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <DropdownShell ref={containerRef} isMobile={isMobile}>
        <p className="text-sm text-destructive text-center p-4">{error}</p>
      </DropdownShell>
    );
  }

  // ── Search results mode ──
  if (!isBrowseMode) {
    const hasResults =
      results.tokens.length > 0 ||
      results.creators.length > 0 ||
      results.lists.length > 0;

    if (!hasResults) {
      return (
        <DropdownShell ref={containerRef} isMobile={isMobile}>
          <p className="text-sm text-muted-foreground text-center p-4">
            No coins, creators, or lists found for "{query}"
          </p>
        </DropdownShell>
      );
    }

    return (
      <DropdownShell ref={containerRef} isMobile={isMobile}>
        <TooltipProvider>
          {/* Tokens */}
          {results.tokens.length > 0 && (
            <div className="px-4 pt-4 pb-2">
              <SectionHeader title="COINS" className="px-0" />
              {results.tokens.map((token, idx) => (
                <MaybeTooltip
                  key={token.mint}
                  stats={[
                    { label: 'Mint', value: truncateMint(token.mint) },
                  ]}
                >
                  <Link
                    to={`/token/${token.mint}`}
                    onClick={onClose}
                    className={rowClass('search-token', idx)}
                  >
                    <FadeImage
                      src={token.imageUrl || undefined}
                      alt={token.name}
                      fallbackSeed={token.symbol || token.name}
                      className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground truncate text-sm">
                          {token.name}
                        </span>
                        <span className="text-xs font-semibold text-muted-foreground flex-shrink-0">
                          {token.symbol}
                        </span>
                      </div>
                      {!isMobile && (
                        <div className="text-xs text-muted-foreground/60 truncate font-mono">
                          {truncateMint(token.mint)}
                        </div>
                      )}
                    </div>
                  </Link>
                </MaybeTooltip>
              ))}
            </div>
          )}

          {/* Creators */}
          {results.creators.length > 0 && (
            <div className="px-4 pt-4 pb-2 border-t border-border">
              <SectionHeader title="CREATORS" className="px-0" />
              {results.creators.map((creator, idx) => (
                <Link
                  key={creator.address}
                  to={getCreatorProfileUrl(
                    creator.address,
                    creator.username
                  )}
                  onClick={onClose}
                  className={rowClass('search-creator', idx)}
                >
                  <FadeImage
                    src={creator.avatarUrl ? getAvatarSrc(creator.avatarUrl, creator.avatarVersion) : undefined}
                    alt={creator.username || 'Creator'}
                    fallbackSeed={creator.username || creator.address}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-foreground text-sm">
                      {creator.username || 'Unknown'}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {creator.tokenCount} coin
                    {creator.tokenCount !== 1 ? 's' : ''}
                  </span>
                </Link>
              ))}
            </div>
          )}

          {/* Lists */}
          {results.lists.length > 0 && (
            <div className="px-4 pt-4 pb-2 border-t border-border">
              <SectionHeader title="LISTS" className="px-0" />
              {results.lists.map((list, idx) => (
                <Link
                  key={list.id}
                  to={`/lists/${list.slug}`}
                  onClick={onClose}
                  className={rowClass('search-list', idx)}
                >
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                    <ListIcon className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground text-sm">
                        {list.name}
                      </span>
                      {list.isDaily && (
                        <Calendar className="w-3 h-3 text-primary" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      /{list.slug}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {list.tokenCount} token
                    {list.tokenCount !== 1 ? 's' : ''}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </TooltipProvider>

        <Footer isMobile={isMobile} />
      </DropdownShell>
    );
  }

  // ── Browse mode: 4 sections ──
  return (
    <DropdownShell ref={containerRef} isMobile={isMobile}>
      <TooltipProvider>
        {/* ── TRENDING ── */}
        {trendingTokens.length > 0 && (
          <div className="px-4 pt-4 pb-2">
            <SectionHeader title="TRENDING" className="px-0 mb-3" />
            <div className="flex overflow-x-auto scrollbar-hide gap-3">
              {trendingTokens.map((token, idx) => {
                const sparkData = sparklines.get(token.mintAddress) || [];
                const raw5m = token.priceChange5m;
                const raw1h = token.priceChange1h;
                const raw24h = token.priceChange24h;
                const priceChange = (raw5m !== null && raw5m !== undefined && raw5m !== 0) ? raw5m
                                  : (raw1h !== null && raw1h !== undefined && raw1h !== 0) ? raw1h
                                  : (raw24h !== null && raw24h !== undefined && raw24h !== 0) ? raw24h
                                  : null;
                const isPos = (priceChange ?? 0) >= 0;
                const pctInfo = formatPercentChange(priceChange);
                const flatIdx = getFlatIndex('trending', idx);

                return (
                  <Link
                    key={token.mintAddress}
                    to={`/token/${token.mintAddress}`}
                    onClick={onClose}
                    className={`w-[152px] flex-shrink-0 bg-secondary/50 border rounded-lg p-3 pb-4 transition-all duration-200 ${
                      flatIdx === selectedIndex
                        ? 'border-primary/50'
                        : 'border-border hover:border-border/80'
                    } hover:bg-secondary/70`}
                  >
                    {/* Header: Avatar + Name */}
                    <div className="flex items-center gap-2 mb-3">
                      <FadeImage
                        src={token.imageUrl || undefined}
                        alt={token.name}
                        fallbackSeed={token.symbol || token.name}
                        className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white truncate leading-tight">
                          {token.name}
                        </div>
                        <div className="text-xs font-semibold text-muted-foreground truncate leading-tight">
                          {token.symbol}
                        </div>
                      </div>
                    </div>

                    {/* Sparkline */}
                    <SparklineSVG
                      data={sparkData}
                      positive={isPos}
                      width={128}
                      height={40}
                      className="mb-3"
                    />

                    {/* Bottom: MC + % */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground">
                        {formatMarketCap(token.fdvUsd)}
                      </span>
                      <span
                        className={`text-[10px] font-medium flex items-center gap-0.5 ${
                          isPos ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {isPos ? (
                          <ArrowUp className="w-2.5 h-2.5" />
                        ) : (
                          <ArrowDown className="w-2.5 h-2.5" />
                        )}
                        {pctInfo.text}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* ── RECENTLY SEARCHED ── */}
        {recentSearches.length > 0 && (
          <div className="px-4 pt-4 pb-2 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <SectionHeader title="RECENTLY SEARCHED" className="px-0" />
              <button
                onClick={clearSearches}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear
              </button>
            </div>
            {recentSearches.map((s, idx) => (
              <div
                key={s.query}
                className={rowClass('recent-search', idx)}
                onClick={() => onSearchSelect?.(s.query)}
              >
                <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="flex-1 text-sm text-foreground truncate">
                  {s.query}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSearch(s.query);
                  }}
                  className="text-muted-foreground hover:text-foreground p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── RECENTLY VIEWED ── */}
        {viewedTokens.length > 0 && (
          <div className="px-4 pt-4 pb-2 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <SectionHeader title="RECENTLY VIEWED" className="px-0" />
              <button
                onClick={clearViewed}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear
              </button>
            </div>
            {viewedTokens.map((token, idx) => (
              <MaybeTooltip key={token.mint}>
                <Link
                  to={`/token/${token.mint}`}
                  onClick={onClose}
                  className={rowClass('recent-viewed', idx)}
                >
                  <FadeImage
                    src={token.imageUrl || undefined}
                    alt={token.name}
                    fallbackSeed={token.symbol || token.name}
                    className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground text-sm truncate">
                        {token.name}
                      </span>
                      <span className="text-xs font-semibold text-muted-foreground flex-shrink-0">
                        {token.symbol}
                      </span>
                      {!isMobile && (
                        <span className="text-xs text-muted-foreground/60 font-mono">
                          {truncateMint(token.mint)}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatTimeAgoWithSuffix(new Date(token.timestamp))}
                  </span>
                </Link>
              </MaybeTooltip>
            ))}
          </div>
        )}

        {/* ── RECENTLY LAUNCHED ── */}
        {launchedTokens.length > 0 && (
          <div className="px-4 pt-4 pb-2 border-t border-border">
            <SectionHeader title="RECENTLY LAUNCHED" className="px-0 mb-2" />
            {launchedTokens.map((token, idx) => {
              const isNew =
                Date.now() - token.createdAt.getTime() < 3600000;

              return (
                <MaybeTooltip key={token.mintAddress}>
                  <Link
                    to={`/token/${token.mintAddress}`}
                    onClick={onClose}
                    className={rowClass('recent-launched', idx)}
                  >
                    <FadeImage
                      src={token.imageUrl || undefined}
                      alt={token.name}
                      fallbackSeed={token.symbol || token.name}
                      className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground text-sm truncate">
                          {token.name}
                        </span>
                        <span className="text-xs font-semibold text-muted-foreground flex-shrink-0">
                          {token.symbol}
                        </span>
                        {!isMobile && (
                          <span className="text-xs text-muted-foreground/60 font-mono">
                            {truncateMint(token.mintAddress)}
                          </span>
                        )}
                        {isNew && (
                          <Badge className="px-1.5 py-px bg-green-400/20 text-green-400 border-0 text-[10px] flex-shrink-0">
                            NEW
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      <span className="text-xs font-medium text-foreground whitespace-nowrap">
                        {formatMarketCap(token.marketCapUsd)}
                      </span>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {formatTimeAgoWithSuffix(token.createdAt)}
                      </span>
                    </div>
                  </Link>
                </MaybeTooltip>
              );
            })}
          </div>
        )}
      </TooltipProvider>

      <Footer isMobile={isMobile} />
    </DropdownShell>
  );
}

// ── Helpers ──

function SectionHeader({
  title,
  className,
}: {
  title: string;
  className?: string;
}) {
  return (
    <h3
      className={`text-xs font-semibold text-muted-foreground uppercase tracking-wider py-1 ${className || 'px-2'}`}
    >
      {title}
    </h3>
  );
}

const DropdownShell = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; isMobile: boolean }
>(({ children, isMobile }, ref) => (
  <div
    ref={ref}
    onMouseDown={(e) => e.preventDefault()}
    className={`bg-card border border-border rounded-lg shadow-lg z-50 flex flex-col ${
      isMobile
        ? 'fixed left-4 right-4 bottom-4 max-h-[80dvh]'
        : 'absolute top-full mt-2 w-[700px] left-1/2 -translate-x-1/2 max-h-[85vh]'
    }`}
    style={isMobile ? { top: 'calc(56px + env(safe-area-inset-top, 0px) + 8px)' } : undefined}
  >
    <div className="flex-1 overflow-y-auto">{children}</div>
  </div>
));
DropdownShell.displayName = 'DropdownShell';

function Footer({ isMobile }: { isMobile: boolean }) {
  if (isMobile) return null;
  return (
    <div className="sticky bottom-0 px-4 py-2 border-t border-border bg-card">
      <p className="text-[10px] text-muted-foreground text-center">
        up/down navigate . Enter select . Esc close
      </p>
    </div>
  );
}
